import { z } from "zod";
import { groupRoleSchema } from "./groups.js";

/* ===========================================================================
 * US-E10.F3-0801/0813 — LiveMemberMoneyStatusShare (@UC0024 @UC0025 @UC0060 @UC0057 · @gate pii,rbac).
 *
 * Owner/Admin/Thủ quỹ gửi cho MỘT phụ huynh một link web nói ba điều: đã đóng bao nhiêu · còn thiếu
 * khoản nào · chuyển vào đâu. Link là transparency link `kind = member_status` (cùng bảng
 * `transparency_links`, cùng token/404 đồng nhất/throttle với link cấp nhóm — KHÔNG cơ chế thứ hai).
 *
 * 🔴🔴 LUẬT NÀY ĐÃ ĐẢO HAI LẦN — đọc hết trước khi "sửa lại cho đúng" (US-E10.F3-0840 · `@UC0231`):
 *   · `0801` 16/09: bấm lại ⇒ *trả lại link cũ* — **bất khả thi**: hash-only, không đọc ngược token.
 *   · `0813` 16/09: bấm lại ⇒ *luôn tạo MỚI*, thu hồi cũ (`superseded`).
 *   · `0840` 21/09 (QĐ người dùng, sau khi bấm thử trên máy): bấm lại ⇒ **GIA HẠN link cũ**. Khác 0801 ở chỗ
 *     quyết định: server KHÔNG đọc ngược token — nó chỉ dời `expiresAt`; URL do **máy người tạo** giữ
 *     (`SecureStorage`, `@UC0232`). Hash-only GIỮ NGUYÊN (`transparency-token.ts`: *"Đừng thêm lại"*).
 *   🔴 Cái MẤT đã cân (QĐ 21/09): nút "Tạo link" KHÔNG còn cắt được link lỡ gửi nhầm ⇒ **"Thu hồi" là đường
 *     DUY NHẤT**; và mỗi lần gia hạn mở thêm một cửa sổ TTL nữa.
 *
 *  - POST   /v1/groups/:id/members/:memberId/status-link → 201 (O/A/T). ĐANG có link hiệu lực ⇒ **gia hạn**
 *           (`expiresAt = now + TTL`, audit `…_renewed`) ⇒ `renewed: true`, `url: null` (hash-only ⇒ không
 *           dựng lại được URL — client hiện lại bản đã lưu trên máy). KHÔNG có link ⇒ tạo mới ⇒ `renewed:
 *           false`, `url` đầy đủ (trả token thô ĐÚNG MỘT LẦN). *  - DELETE /v1/groups/:id/members/:memberId/status-link → 204 (thu hồi link đang hiệu lực).
 *  - GET    /v1/t/:token (PUBLIC) → `kind: "member_status"` — dữ liệu của ĐÚNG suất đó, SỐNG (lấy lúc mở).
 *
 * 🔴 KHÔNG có `GET /v1/groups/:id/members/:memberId/status-link` — với hash-only không thể trả lại URL
 * của link đang sống, và mobile chưa từng gọi endpoint này (gỡ trước khi 0803 merge).
 * 🔴 KHÔNG PII: không phone/email/địa chỉ, không số dư quỹ, không tên người khác. `.strict()` mọi tầng.
 * 🔴 Trang web KHÔNG ghi gì về server (QĐ người dùng 16/09/2026): chỉ có GET này.
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Hạn của link — **2 ngày** (QĐ người dùng 21/09/2026, `@UC0230`; trước đó 30 ngày từ QĐ 16/09).
 *
 * Server đặt `expiresAt = now + TTL` ở CẢ hai đường (tạo mới · gia hạn); client chỉ hiển thị, không tự tính.
 * 🔴 CỐ ĐỊNH trong code — cố ý KHÔNG cấu hình hai mức như `invite_link_ttl_days` của link mời nhóm (QĐ 21/09):
 * một con số cho một bài toán; đổi thì sửa đây + deploy.
 */
export const MEMBER_STATUS_LINK_TTL_DAYS = 2;

/**
 * Link của suất sau một lượt POST.
 *
 * 🔴 `url` **nullable từ 0840** (`@UC0231`): chỉ có ở lượt TẠO MỚI (`renewed: false`) — token thô trả đúng một
 * lần. Lượt GIA HẠN (`renewed: true`) trả `null` vì server giữ hash-only, không dựng lại được URL; client hiện
 * lại bản nó đã lưu trên máy (`@UC0232`). Cặp `(renewed, url)` là bất biến: `renewed === (url === null)` —
 * `superRefine` dưới đây ép, để không có lượt nào vừa "mới" vừa không URL (hoặc ngược lại).
 */
export const memberStatusLinkSchema = z
  .object({
    id: z.string().uuid(),
    url: z.string().url().nullable(),
    expiresAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    /** `true` = link CŨ được dời hạn (không phát token mới). Vắng ⇒ `false` (giữ tương thích client cũ). */
    renewed: z.boolean().default(false),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.renewed && value.url !== null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["url"], message: "renewed ⇒ url phải null (hash-only)" });
    }
    if (!value.renewed && value.url === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["url"], message: "link mới BẮT BUỘC có url (token trả 1 lần)" });
    }
  });
export type MemberStatusLink = z.infer<typeof memberStatusLinkSchema>;

/** POST → 201. `renewed: false` = link MỚI (kèm `url`); `renewed: true` = link CŨ vừa được dời hạn (`url: null`). */
export const createMemberStatusLinkResponseSchema = memberStatusLinkSchema;
export type CreateMemberStatusLinkResponse = z.infer<typeof createMemberStatusLinkResponseSchema>;

/**
 * Một khoản còn thiếu — tên đợt (`null` khi đợt không đặt tên ⇒ web dùng nhãn chung) + hạn + số CÒN
 * THIẾU (đã trừ phần đóng một phần — cùng `outstandingMinorOf` với màn Chi tiết thành viên).
 *
 * 🔴 `periodIndex` — THÊM 17/09/2026 (bug đã đo thật): lịch định kỳ (VD thu tuần) sinh nhiều đợt
 * CÙNG TÊN, mỗi đợt là một dòng riêng ở đây — người xem thấy 4 dòng "1231212332" giống hệt nhau,
 * không biết dòng nào là kỳ nào. Server ĐÃ đếm số này cho `outstanding[]` từ US-E24.F51-0503 (cùng
 * `loadPeriodIndexes` với sheet Nộp quỹ/màn tiến độ — MỘT nguồn, "Kỳ 3" phải chỉ đúng một kỳ ở mọi
 * bề mặt) nhưng trang public trước bản vá này chưa forward field đó. `null` = đợt thu MỘT LẦN
 * (không thuộc lịch định kỳ nào) — web không hiện "· Kỳ n" cho các đợt này.
 */
export const memberStatusOutstandingItemSchema = z
  .object({
    title: z.string().nullable(),
    dueDate: z.string().regex(ISO_DATE_PATTERN).nullable(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    periodIndex: z.number().int().positive().nullable(),
  })
  .strict();
export type MemberStatusOutstandingItem = z.infer<typeof memberStatusOutstandingItemSchema>;

/**
 * Một khoản đã nộp. `kind = due` ⇒ `title` là tên đợt; `kind = other_income` ⇒ đóng góp thêm ngoài
 * đợt (`title` = ghi chú người ghi, `null` khi không có) — web hiện *"Quỹ chung <tên nhóm>"* cho loại
 * này (cùng câu với mobile US-MOB.F9-0794). Backend KHÔNG bịa câu chữ: chỉ trả loại + dữ liệu.
 *
 * 🔴 `periodIndex` — THÊM 17/09/2026, cùng lý do với `memberStatusOutstandingItemSchema`: đợt định kỳ
 * ĐÃ ĐÓNG cũng lặp tên qua nhiều dòng lịch sử. Luôn `null` với `kind = other_income` (không thuộc
 * đợt/lịch nào).
 */
export const memberStatusHistoryItemSchema = z
  .object({
    kind: z.enum(["due", "other_income"]),
    title: z.string().nullable(),
    paidAt: z.string().datetime().nullable(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    periodIndex: z.number().int().positive().nullable(),
  })
  .strict();
export type MemberStatusHistoryItem = z.infer<typeof memberStatusHistoryItemSchema>;

/**
 * Tài khoản nhận tiền của nhóm — ba ô text + ảnh QR (URL ký NGẮN HẠN, không phải key R2) + nội dung CK
 * gợi ý cho đúng suất (`buildTransferContent`). Toàn khối `null` ở response khi nhóm CHƯA cấu hình gì
 * (0 QR, 0 ô chữ) ⇒ web ẨN khối.
 *
 * 🔴 Ba ô text (`bankName`/`accountNumber`/`accountName`) NULLABLE riêng từng ô — SỬA 17/09/2026, bug
 * đã đo thật: nhóm up QR TRƯỚC khi gõ 3 ô chữ (ảnh VietQR tự mang tên NH/STK, luồng thật, không phải
 * ngoại lệ) từng bị cả khối `bankAccount` báo `null`, ẩn LUÔN cả QR đã có — vì service cũ chỉ nhìn 3 ô
 * chữ, bỏ qua QR. "Đã cấu hình" trên toàn repo lấy QR làm chuẩn (`needsConfig = !qrImageKey`, xem
 * `GroupBankService`), nên khối này giờ hiện khi có QR HOẶC bất kỳ ô chữ nào; ô nào trống ⇒ `null` ⇒
 * web vắng mặt đúng ô đó (không hiện chuỗi rỗng).
 */
export const memberStatusBankAccountSchema = z
  .object({
    bankName: z.string().nullable(),
    accountNumber: z.string().nullable(),
    accountName: z.string().nullable(),
    qrImageUrl: z.string().url().nullable(),
    transferContent: z.string().nullable(),
  })
  .strict();
export type MemberStatusBankAccount = z.infer<typeof memberStatusBankAccountSchema>;

/**
 * Trang public của MỘT suất. `generatedAt` = giờ server lúc dựng ("Số liệu tính đến …") — link SỐNG,
 * không snapshot. `member.representative` = người lớn đại diện (nhóm phụ huynh), `null` với nhóm thường.
 */
export const memberStatusPublicViewResponseSchema = z
  .object({
    kind: z.literal("member_status"),
    groupName: z.string(),
    currency: z.string().length(3),
    generatedAt: z.string().datetime(),
    member: z
      .object({
        displayName: z.string(),
        role: groupRoleSchema,
        representative: z.string().nullable(),
      })
      .strict(),
    totals: z
      .object({
        paidMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
        outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
      })
      .strict(),
    outstanding: z.array(memberStatusOutstandingItemSchema),
    /**
     * Mới → cũ, tối đa 200 khoản gần nhất (`HISTORY_CAP` ở service — trang public không phân trang;
     * suất có hơn 200 khoản đã nộp là ngoại lệ, phần cũ hơn xem trong app). `other_income` luôn
     * `title: null` trên trang public (memo tự do của thủ quỹ không rời API — review #806 · M4).
     */
    history: z.array(memberStatusHistoryItemSchema),
    /**
     * 🔴 QĐ người dùng 08/10/2026 — `true` khi lịch sử đã nộp bị cắt ở trần `HISTORY_CAP` (còn khoản cũ hơn chưa gửi). Web hiện HẾT mọi dòng
     * được gửi (bỏ cắt 3 dòng ở tầng hiển thị, US-E10.F3-0841) và dùng cờ này để nói thật phần bị cắt, thay vì im lặng thiếu số.
     */
    historyTruncated: z.boolean(),
    bankAccount: memberStatusBankAccountSchema.nullable(),
  })
  .strict();
export type MemberStatusPublicViewResponse = z.infer<typeof memberStatusPublicViewResponseSchema>;

/**
 * Từ khoá KHÔNG được xuất hiện ở bất kỳ key nào (đệ quy) của payload `member_status` — guard trong
 * test contracts + int-spec. `balance` nằm trong danh sách vì trang của MỘT suất không được mang số dư
 * QUỸ (khác link cấp nhóm có `show_balance`).
 */
export const MEMBER_STATUS_FORBIDDEN_KEY_PATTERN = /phone|email|address|balance|token|cipher|hash/i;

/** Duyệt đệ quy mọi key của một object JSON — dùng cho guard "không có key cấm". */
export function collectObjectKeys(value: unknown, out: string[] = []): string[] {
  if (Array.isArray(value)) {
    for (const item of value) collectObjectKeys(item, out);
  } else if (value !== null && typeof value === "object") {
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
      out.push(key);
      collectObjectKeys(inner, out);
    }
  }
  return out;
}
